import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, {
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
  useState
} from 'react';

import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import Icon from 'dpl/common/components/Icon';

const DRAWER_TRANSITION_DURATION = 250;

const MenuDrawer = forwardRef(
  (
    { classes, children, title, onClose, position, maxWidth, hideHeader },
    ref
  ) => {
    const [isTransitioning, setIsTransitioning] = useState(true);

    function handleClose() {
      setIsTransitioning(true);
      setTimeout(onClose, DRAWER_TRANSITION_DURATION);
    }

    useEffect(() => {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 1);

      return () => clearTimeout(timer);
    }, []);

    const transform = useMemo(() => {
      if (!isTransitioning) return 'translate(0%, 0%)';
      if (position === 'right') return 'translateX(100%)';
      if (position === 'left') return 'translateX(-100%)';
      if (position === 'top') return 'translateY(-100%)';
      if (position === 'bottom') return 'translateY(100%)';
      return null;
    }, [isTransitioning, position]);

    useImperativeHandle(ref, () => ({ close: handleClose }), []);

    return (
      <MiniOverlayModal
        className={classnames(
          'MenuDrawer',
          { 'bg-white': !isTransitioning },
          classes.modal
        )}
        closeButtonClassName={classnames(
          'transition bg-white ba b--light-gray br-100 padding-h-6 padding-v-6 mt5 mt4-md mr4',
          { 'o-0': isTransitioning }
        )}
        contentClassName=""
        onCloseClick={handleClose}
        maxWidth={maxWidth}
        scrollableContent
        hideCloseButton
      >
        <div
          className={classnames(
            'bg-white min-h-100',
            { 'o-0': isTransitioning },
            classes.wrapper
          )}
          style={{
            transitionProperty: 'transform, opacity',
            transitionDuration: `${DRAWER_TRANSITION_DURATION}ms`,
            transform
          }}
        >
          {!hideHeader && (
            <div
              className={classnames(
                'flex justify-between items-center gap-16 sticky top-0 bg-white bb b--light-gray pt4 pb3 ph5 z-1',
                classes.header
              )}
            >
              {title && <h4 className="font-24 truncate">{title}</h4>}
              <button
                className="transition secondary bg-white ba b--light-gray br-100 ph2 pv2 ml-auto"
                type="button"
                onClick={handleClose}
              >
                <Icon name="fetch-close" height="22px" width="22px" />
              </button>
            </div>
          )}
          <div className={classnames('pt6 pb6', classes.content)}>
            {children}
          </div>
        </div>
      </MiniOverlayModal>
    );
  }
);

MenuDrawer.displayName = 'MenuDrawer';

MenuDrawer.propTypes = {
  classes: PropTypes.shape({
    modal: PropTypes.string,
    header: PropTypes.string,
    wrapper: PropTypes.string,
    content: PropTypes.string
  }),
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  position: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
  maxWidth: PropTypes.string,
  hideHeader: PropTypes.bool
};

MenuDrawer.defaultProps = {
  classes: {
    modal: null,
    header: null,
    wrapper: null,
    content: null
  },
  maxWidth: '600px',
  title: null,
  position: null,
  hideHeader: false
};

export default MenuDrawer;
